import React, { useState } from 'react';
import elementsData from './elements.json'; // The JSON array

const elements = elementsData.elements;

const categoryColors = {
    'alkali metal': '#FFE082',
    'alkaline earth metal': '#B9F6CA',
    'transition metal': '#82B1FF',
    'post-transition metal': '#CE93D8',
    'metalloid': '#FFD54F',
    'nonmetal': '#A5D6A7',
    'noble gas': '#B2EBF2',
    'lanthanoid': '#FFAB91',
    'lanthanide': '#FFAB91', // American spelling
    'actinoid': '#B0BEC5',
    'actinide': '#B0BEC5', // American spelling
    'unknown': '#E0E0E0',
    'unknown, probably transition metal': '#E0E0E0'
  };
  
  const categoryNames = {
    'alkali metal': 'Alkali Metal',
    'alkaline earth metal': 'Alkaline Earth Metal',
    'transition metal': 'Transition Metal',
    'post-transition metal': 'Post-Transition Metal',
    'metalloid': 'Metalloid',
    'nonmetal': 'Nonmetal',
    'noble gas': 'Noble Gas',
    'lanthanoid': 'Lanthanoid',
    'lanthanide': 'Lanthanoid', // American spelling displayed UK-style
    'actinoid': 'Actinoid',
    'actinide': 'Actinoid', // American spelling displayed UK-style
    'unknown': 'Unknown',
    'unknown, probably transition metal': 'Unknown (likely transition metal)'
  };
  
  const categoryExplanations = {
    'alkali metal': "Alkali metals (Group 1) are soft, highly reactive metals. They include lithium, sodium, potassium, and are known for reacting explosively with water.",
    'alkaline earth metal': "Alkaline earth metals (Group 2) are harder than alkali metals and somewhat less reactive. Examples: magnesium, calcium.",
    'transition metal': "Transition metals are found in the middle of the table (Groups 3–12). They’re strong, often shiny, and used in coins, wires, and jewellery.",
    'post-transition metal': "Post-transition metals are softer metals found after the transition metals. They include aluminium, tin, and lead.",
    'metalloid': "Metalloids have properties in between metals and nonmetals. They’re often semiconductors—like silicon, which is used in computer chips.",
    'nonmetal': "Nonmetals are elements that usually form gases or brittle solids at room temperature. Examples: carbon, nitrogen, oxygen.",
    'noble gas': "Noble gases are colourless, odourless gases (Group 18). They almost never react with other elements. Examples: helium, neon, argon.",
    'lanthanoid': "Lanthanoids (also called lanthanides) are rare earth metals, often used in electronics and magnets. They’re found in the row below the main table.",
    'lanthanide': "Lanthanoids (also called lanthanides) are rare earth metals, often used in electronics and magnets. They’re found in the row below the main table.",
    'actinoid': "Actinoids (also called actinides) are mostly radioactive metals, including uranium and plutonium. They’re found in the second row below the main table.",
    'actinide': "Actinoids (also called actinides) are mostly radioactive metals, including uranium and plutonium. They’re found in the second row below the main table.",
    'unknown': "This element’s properties haven’t been confirmed. Many are man-made and exist for only fractions of a second in labs.",
    'unknown, probably transition metal': "Scientists believe this element acts like a transition metal, but it hasn’t been observed in enough detail to be sure. Many superheavy elements are too unstable to study fully.",
    'diatomic nonmetal': "Diatomic nonmetals naturally form molecules made of two atoms. Examples: hydrogen (H₂), nitrogen (N₂), oxygen (O₂), and halogens.",
    'polyatomic nonmetal': "Polyatomic nonmetals naturally form molecules with more than two atoms, like phosphorus (P₄) and sulfur (S₈)."
  };
  

function PeriodicTable() {
  const [selected, setSelected] = useState(null);

  return (
    <div style={{ maxWidth: 1600, margin: '0 auto', padding: 24 }}>
      <h2 style={{ textAlign: 'center', fontSize: '2.6rem', marginBottom: 28 }}>Periodic Table of Elements</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(18, 70px)',
          gridAutoRows: '70px',
          gap: '5px',
          background: '#f5f7fb',
          padding: 16,
          borderRadius: 16,
          boxShadow: '0 2px 16px rgba(0,0,0,0.10)'
        }}
      >
        {elements.map(el => (
          <div
            key={el.number}
            onClick={() => setSelected(el)}
            style={{
              gridColumn: el.xpos,
              gridRow: el.ypos,
              background: categoryColors[el.category] || '#f0f0f0',
              border: selected && selected.number === el.number ? '3px solid #0072ce' : '2px solid #fff',
              borderRadius: 8,
              cursor: 'pointer',
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 600,
              boxShadow: selected && selected.number === el.number ? '0 0 12px #0072ce80' : '0 2px 4px #0001',
              position: 'relative',
              userSelect: 'none',
              transition: 'transform 0.1s, box-shadow 0.2s, border 0.2s',
              outline: selected && selected.number === el.number ? '2px solid #3332' : undefined,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
            }}
            title={el.name}
            tabIndex={0}
          >
            <span style={{ fontSize: 32, fontWeight: 'bold', lineHeight: 1 }}>{el.symbol}</span>
            <span style={{ fontSize: 13, fontWeight: 400, color: '#444' }}>{el.number}</span>
            <span style={{
              fontSize: 10,
              color: '#666',
              background: '#fff6',
              borderRadius: 5,
              padding: '1px 6px',
              marginTop: 2,
              maxWidth: 54,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }}>
              {el.category && el.category.toLowerCase().includes('unknown')
                ? 'Unknown'
                : (categoryNames[el.category] || el.category)}
            </span>
          </div>
        ))}
      </div>

      {selected && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.44)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 18,
              boxShadow: '0 8px 36px rgba(0,0,0,0.25)',
              maxWidth: 640,
              width: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              padding: 38,
              border: `4px solid ${categoryColors[selected.category] || '#0072ce'}`,
            }}
            onClick={e => e.stopPropagation()} // prevent closing modal when clicking inside
          >
            <button
              style={{
                position: 'absolute',
                top: 12,
                right: 16,
                fontSize: 22,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#0072ce',
                fontWeight: 'bold',
              }}
              aria-label="Close"
              onClick={() => setSelected(null)}
            >✖️</button>
            <h3 style={{ fontSize: 36, margin: 0, color: '#333' }}>
              {selected.name} <span style={{ color: '#0072ce' }}>({selected.symbol})</span>
            </h3>
            {/* --- Row: Category badge, Atomic #, Mass --- */}
            <div style={{
              margin: '12px 0 10px 0',
              fontSize: 18,
              color: '#0072ce',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap'
            }}>
              <span style={{
                background: '#f0f9ff',
                borderRadius: 7,
                padding: '4px 12px',
                border: `2px solid ${categoryColors[selected.category] || '#0072ce'}`,
                fontWeight: 600,
                fontSize: 16,
                minWidth: 120,
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}>
                {categoryNames[selected.category] || selected.category}
              </span>
              <span>Atomic #: {selected.number}</span>
              <span>Mass: {selected.atomic_mass}</span>
            </div>

            {/* --- Explanation Block (full width, below row) --- */}
            <div style={{
              margin: '0 0 18px 0',
              fontSize: 15,
              color: '#276ef1',
              background: '#eef6fa',
              borderRadius: 8,
              padding: '10px 18px'
            }}>
              <strong>
                What is a {categoryNames[selected.category]?.toLowerCase() || selected.category}?
              </strong>
              <div>
                {categoryExplanations[selected.category] ||
                  "No explanation available yet!"}
              </div>
            </div>

            <p style={{ fontStyle: 'italic', color: '#333', margin: '0 0 16px 0', lineHeight: 1.4 }}>
              {selected.summary}
            </p>
            <div style={{ fontSize: 16, color: '#444', marginBottom: 16, lineHeight: 1.6 }}>
              <div><strong>Appearance:</strong> {selected.appearance || 'N/A'}</div>
              <div><strong>Discovered by:</strong> {selected.discovered_by || 'Unknown'}</div>
              <div><strong>Named by:</strong> {selected.named_by || 'Unknown'}</div>
              <div><strong>Phase:</strong> {selected.phase || 'N/A'}</div>
              <div><strong>Density:</strong> {selected.density || 'N/A'}</div>
              <div><strong>Melting Point:</strong> {selected.melt ? `${selected.melt} K` : 'N/A'}</div>
              <div><strong>Boiling Point:</strong> {selected.boil ? `${selected.boil} K` : 'N/A'}</div>
              <div><strong>Electron Configuration:</strong> {selected.electron_configuration || 'N/A'}</div>
            </div>
            {/* Add more info as you like */}
          </div>
        </div>
      )}
    </div>
  );
}

export default PeriodicTable;
